import React, { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, MessageCircle, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { track, EVENT } from "../../lib/publicAnalytics";

/**
 * AskAIWidget — Phase 2 upgrade.
 *
 * Behavior: rule-based, browser-only, intent-matched responses. NO LLM call.
 * NO access to: customer data, admin data, CRM, internal notes, /api/analyses.
 * Designed provider-agnostic so a future Phase can swap to OpenAI / Anthropic /
 * MS Copilot by replacing pickReply() with an async API call.
 *
 * The intent table answers public/product questions and routes to safe pages.
 */

const DEFAULT_REPLY = "I can help with product questions — what GovBidIQ does, how it scores prospects, which set-aside strategies fit, and so on. Or I can point you to AI Discovery, the Product Tour, a Sample Report, the Demo Library, the Academy, FAQ, or a Strategy Call.";

const SAFETY_DENY = "I can't help with that on the public assistant. For legal, financial, or official procurement advice please consult a qualified professional. For specific account data, please log in.";

// Each intent has either a `reply` (static text) or a `replyFn` (text builder),
// and zero or more `actions` (link buttons).
const INTENTS = [
  // ── PRODUCT EXPLAINER ─────────────────────────────────────────────
  { match: /(what is|what's) (gov\s*bid\s*iq|govbidiq)|what does govbidiq do/i,
    reply: "GovBidIQ is an AI-powered government contract intelligence platform. It scores prospects and opportunities on a 100-point scale, surfaces top reasons + risks, and recommends a concrete next action — so capture teams stop drowning in spreadsheets.",
    actions: [{ label: "Take the Product Tour", to: "/tour" }, { label: "View Sample Report", to: "/sample" }] },

  { match: /(who is it for|who can use|who should use|target user)/i,
    reply: "Federal contractors who pursue more opportunities than they can humanly qualify — SDVOSB, 8(a), WOSB, HUBZone, small businesses, primes, subs, capture and proposal teams, and consultants.",
    actions: [{ label: "See Use Cases", to: "/use-cases" }] },

  { match: /how (does|do) (it|govbidiq) help|how can govbidiq help|how (does|do) it work/i,
    reply: "Six steps: identify → generate → review → score → act → decide. Each step takes seconds, not weeks.",
    actions: [{ label: "How It Works", to: "/how-it-works" }, { label: "Take the Tour", to: "/tour" }] },

  // ── CONCEPTS ──────────────────────────────────────────────────────
  { match: /go.?no.?go|bid.?no.?bid|pursuit decision/i,
    reply: "A go/no-go (bid/no-bid) decision answers: should we spend capture and proposal resources on this opportunity? GovBidIQ scores it on 10 factors, surfaces Top 5 reasons + Top 3 risks, and recommends pursue / pursue with caution / no-bid.",
    actions: [{ label: "View Sample Report", to: "/sample" }] },

  { match: /(prospect intelligence|company intelligence|prospect scor)/i,
    reply: "Prospect Intelligence scores companies on 5 factors (federal pursuit, small business profile, pain, accessibility, buyer likelihood) and recommends a priority band — Immediate, This Week, This Month, or Nurture.",
    actions: [{ label: "Demo: Prospect Intelligence", to: "/demo-library" }] },

  { match: /opportunity scor|how (do|does) you score|how (is|are) opportunit/i,
    reply: "Opportunity Score is a 10-factor scorecard (fit, capability, revenue, competitive position, compliance burden, resource demand, complexity, past performance, risk, win probability). Each factor 0–10. Total drives the recommendation.",
    actions: [{ label: "View Sample Report", to: "/sample" }] },

  { match: /(competitive intelligence|incumbent|recompete)/i,
    reply: "Every report surfaces incumbent indicators, recompete signals, and probable-incumbent-advantage scoring so you go in eyes-open about who you're displacing.",
    actions: [{ label: "Sample Report", to: "/sample" }, { label: "Demo Library", to: "/demo-library" }] },

  { match: /(teaming|partner discovery|sub finder)/i,
    reply: "Teaming Partner Discovery surfaces partners by NAICS, set-aside, geography, and past performance — so you can close capability gaps fast.",
    actions: [{ label: "Demo: Teaming", to: "/demo-library" }] },

  // ── SET-ASIDE STRATEGIES ──────────────────────────────────────────
  { match: /sdvosb/i,        reply: "SDVOSB firms get dedicated DoD/VA set-aside utilization signals + sole-source candidate detection.",                     actions: [{ label: "SDVOSB Use Case", to: "/use-cases" }] },
  { match: /\b8\(?a\)?\b/i,  reply: "8(a) firms get capture-window scoring, sole-source candidate detection, and mentor-protégé teaming suggestions.",        actions: [{ label: "8(a) Use Case", to: "/use-cases" }] },
  { match: /\bwosb\b/i,      reply: "WOSB firms get agency-by-agency WOSB utilization signals + recompete prediction.",                                      actions: [{ label: "WOSB Use Case", to: "/use-cases" }] },
  { match: /hubzone/i,       reply: "HUBZone firms get geo-aware fit scoring + HUBZone-set-aside opportunity surfacing.",                                    actions: [{ label: "HUBZone Use Case", to: "/use-cases" }] },
  { match: /(prime contractor|^prime\b)/i, reply: "Primes use GovBidIQ for pipeline grooming and competitive landscape across hundreds of opportunities.",   actions: [{ label: "Primes Use Case", to: "/use-cases" }] },
  { match: /(subcontract|^sub\b|sub-?contractor)/i, reply: "Subs use GovBidIQ to profile target primes by award history and past performance.",              actions: [{ label: "Subcontractor Use Case", to: "/use-cases" }] },

  // ── PRODUCT FLOW / CTAs ───────────────────────────────────────────
  { match: /(start|begin|kick off).*(discovery|qualif)/i, reply: "AI Discovery is a 90-second guided questionnaire that ends with a personal Fit Summary.", actions: [{ label: "Start AI Discovery", to: "/discovery" }] },
  { match: /(discovery|qualif|fit summary)/i,             reply: "AI Discovery gives you a Fit Summary with best-fit features, recommended videos, and a next step.", actions: [{ label: "Start AI Discovery", to: "/discovery" }] },
  { match: /(tour|demo|walk|see how)/i,                   reply: "I can show you a guided product tour — about 3 minutes.",            actions: [{ label: "Take the Product Tour", to: "/tour" }, { label: "Demo Library", to: "/demo-library" }] },
  { match: /(video|watch|library)/i,                      reply: "The Demo Library has 12 short clips covering every part of the platform.", actions: [{ label: "Open Demo Library", to: "/demo-library" }] },
  { match: /(price|cost|how much|tier|plan)/i,            reply: "Pricing is published. Three tiers — pick the smallest that fits.",   actions: [{ label: "View Pricing", to: "/pricing" }] },
  { match: /(sample|example|what does a report look like|format)/i, reply: "Here's a fully synthetic Opportunity Intelligence Report — exactly the format real customers receive.", actions: [{ label: "View Sample Report", to: "/sample" }] },
  { match: /(faq|question|common question)/i,             reply: "Most visitor questions are answered in our FAQ.",                    actions: [{ label: "Read FAQ", to: "/faq" }] },
  { match: /(learn|academy|education|teach|article)/i,    reply: "The GovBidIQ Academy covers FAR, NAICS, SAM.gov, CPARS, set-aside strategy, capture, and proposal compliance.", actions: [{ label: "Open the Academy", to: "/learning" }] },
  { match: /(use case|industry|fit)/i,                    reply: "We support SDVOSB, 8(a), WOSB, HUBZone, primes, subs, consultants, and capture / proposal teams.",            actions: [{ label: "See Use Cases", to: "/use-cases" }] },
  { match: /(call|talk|human|sales|strategy|book|schedule)/i, reply: "A 25-minute Strategy Call is the fastest way to evaluate fit.", actions: [{ label: "Request a Strategy Call", to: "/strategy-call" }] },

  // ── CUSTOMER LIFECYCLE ────────────────────────────────────────────
  { match: /(after i (become|sign up|subscribe)|once i'?m a customer|customer experience)/i,
    reply: "Customers get a private dashboard, saved analyses, share links for their team, and a customer-side AI copilot scoped to their own reports (coming in V2 Phase 3).",
    actions: [{ label: "Pricing", to: "/pricing" }] },

  { match: /(my data|data priva|secur|confiden)/i,
    reply: "Customer reports are accessible only via your share token today and via your authenticated customer account in V2 Phase 3. No customer data appears on this public site. Sample reports are 100% synthetic.",
    actions: [{ label: "Read FAQ", to: "/faq" }] },

  // ── SAFETY DENIES ─────────────────────────────────────────────────
  { match: /(legal advice|attorney|lawyer|sue|lawsuit)/i,     reply: SAFETY_DENY, deny: true },
  { match: /(financial advice|investment|tax advice)/i,       reply: SAFETY_DENY, deny: true },
  { match: /(guarantee|will i win|will we win|win rate|conversion rate)/i, reply: "I can't guarantee award outcomes — that depends on your capture, pricing, and proposal. What I can do is help you decide which opportunities deserve your capture energy.", deny: true },
  { match: /(my account|my customer|my report|my company data|admin|crm)/i, reply: "I'm the public assistant — I don't have access to account or admin data. For your own reports, please log in.", deny: true },
];

function pickReply(text) {
  const t = (text || "").trim();
  if (!t) return { reply: DEFAULT_REPLY, actions: [], deny: false };
  for (const i of INTENTS) {
    if (i.match.test(t)) {
      return {
        reply: typeof i.reply === "function" ? i.reply(t) : i.reply,
        actions: i.actions || [],
        deny: !!i.deny,
      };
    }
  }
  return { reply: DEFAULT_REPLY, actions: [], deny: false };
}

const QUICK = [
  { label: "What is GovBidIQ?",     text: "What is GovBidIQ?" },
  { label: "Start AI Discovery",    text: "start ai discovery" },
  { label: "See a Sample Report",   text: "see a sample report" },
  { label: "How does scoring work?", text: "how does opportunity scoring work" },
];

export default function AskAIWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi — I'm GovBidIQ's public assistant. Ask about the product, set-aside fit, scoring, or pricing.", actions: [], deny: false },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const onOpen = () => { setOpen(true); track(EVENT.ASK_AI_OPENED, {}); };
  const respond = (text) => {
    const t = text.trim();
    if (!t) return;
    track(EVENT.ASK_AI_PROMPT_SENT, { length: t.length });
    const { reply, actions, deny } = pickReply(t);
    setMessages((m) => [...m, { role: "user", text: t }, { role: "assistant", text: reply, actions, deny }]);
    setInput("");
  };
  const onSend = (e) => { e?.preventDefault(); respond(input); };

  if (!open) {
    return (
      <button onClick={onOpen} data-testid="ask-ai-open" className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-3 shadow-2xl shadow-sky-500/30">
        <Sparkles className="w-4 h-4" strokeWidth={2.5} /> Ask AI
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-40 w-[380px] max-w-[calc(100vw-2rem)] border border-sky-500/40 bg-[var(--bg-primary)] flex flex-col shadow-2xl shadow-sky-500/20" data-testid="ask-ai-panel">
      <div className="flex items-center justify-between border-b border-[var(--border-soft)] px-4 py-3 bg-[var(--bg-secondary)]">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-sky-400" strokeWidth={2.5} />
          <div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400">/ GovBidIQ Guide</div>
            <div className="font-mono text-[9px] text-slate-500">Public · no private data</div>
          </div>
        </div>
        <button onClick={() => setOpen(false)} data-testid="ask-ai-close" className="text-slate-500 hover:text-slate-200" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5 max-h-[440px]">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            <div className={`text-sm leading-relaxed px-3 py-2 max-w-[88%] ${
              m.role === "user"
                ? "bg-sky-500/15 border border-sky-500/40 text-slate-100"
                : m.deny
                  ? "bg-rose-500/5 border border-rose-500/30 text-slate-200"
                  : "bg-slate-900/70 border border-slate-800 text-slate-200"
            }`}>
              {m.deny && (
                <div className="font-mono text-[9px] tracking-widest uppercase text-rose-300 mb-1 inline-flex items-center gap-1">
                  <ShieldAlert className="w-2.5 h-2.5" /> Public Boundary
                </div>
              )}
              <div>{m.text}</div>
              {m.actions && m.actions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.actions.map((a, k) => (
                    <Link key={k} to={a.to} onClick={() => setOpen(false)} data-testid={`ask-ai-action-${a.to.replace(/\//g, "-")}`} className="font-mono text-[10px] tracking-widest uppercase text-sky-300 hover:text-sky-200 border border-sky-500/40 hover:border-sky-400 px-2.5 py-1">
                      {a.label} →
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {messages.length <= 1 && (
          <div className="pt-1">
            <div className="font-mono text-[9px] tracking-widest uppercase text-slate-500 mb-1.5">/ Try asking</div>
            <div className="flex flex-wrap gap-1.5">
              {QUICK.map((q) => (
                <button key={q.label} onClick={() => respond(q.text)} data-testid={`ask-ai-quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`} className="font-mono text-[10px] tracking-widest uppercase text-slate-300 hover:text-sky-300 border border-slate-700 hover:border-sky-500 px-2.5 py-1.5">
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <form onSubmit={onSend} className="border-t border-[var(--border-soft)] p-2 flex gap-2 bg-[var(--bg-secondary)]">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about the product, scoring, or set-asides…" className="flex-1 bg-transparent border border-slate-700 focus:border-sky-500 outline-none text-slate-100 text-sm px-2.5 py-2 font-mono" data-testid="ask-ai-input" aria-label="Ask AI question" />
        <button type="submit" data-testid="ask-ai-send" className="bg-sky-500 hover:bg-sky-400 text-slate-950 px-3 inline-flex items-center" aria-label="Send">
          <Send className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </form>
    </div>
  );
}
